import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import useSkipExternalDependency from "shared/Run/useSkipExternalDependency";
import { RunExternalDependencyStatus } from "types/generated";
import { AccessResult } from "utils/access";
import Tooltip from "ds/components/Tooltip";

type SkipExternalDependencyButtonProps = {
  dependencyId: string;
  runQueryToRefetch?: string;
  canSkipExternalDependencies: AccessResult;
};

const SkipExternalDependencyButton = ({
  dependencyId,
  runQueryToRefetch,
  canSkipExternalDependencies,
}: SkipExternalDependencyButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const [skipExternalDependency, { loading, data }] = useSkipExternalDependency({
    refetchQueries: [runQueryToRefetch || "GetRun"],
  });

  const onClick = useCallback(
    () =>
      skipExternalDependency(
        { dependency: dependencyId, status: RunExternalDependencyStatus.Skipped },
        (data) => {
          if (
            data?.runExternalDependencyMarkAsCompleted?.status ===
            RunExternalDependencyStatus.Skipped
          ) {
            reportSuccess({
              message: `External dependency successfully skipped`,
            });
          }
        }
      ),
    [skipExternalDependency, reportSuccess, dependencyId]
  );

  const isLoading = loading || !!data?.runExternalDependencyMarkAsCompleted?.status;

  if (!canSkipExternalDependencies.result && !canSkipExternalDependencies.message) {
    return null;
  }

  return (
    <Tooltip
      active={!!canSkipExternalDependencies.message}
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          size="small"
          disabled={isLoading || !canSkipExternalDependencies.result}
          loading={isLoading}
          onPress={onClick}
        >
          Skip
        </Button>
      )}
    >
      {canSkipExternalDependencies.message}
    </Tooltip>
  );
};

export default SkipExternalDependencyButton;
